import { GAME_STATUS } from '@crazygames/game-types/src/game/game-status.type';
import { SUBMISSION_STATUS } from '@crazygames/game-types/src/submission/submission-status.type';

const BL_ENDED_ON = 'Basic Launch has ended on';
const BL_WILL_END_ON = 'Basic Launch expires on';

export const basicLaunchEndLabel = (hasExpired: boolean) => (hasExpired ? BL_ENDED_ON : BL_WILL_END_ON);

// added isAdmin to allow different descriptions for devs and admins (i.e. not showing "mixed results" for devs)
export function readableStatus(status: SUBMISSION_STATUS, isAdmin: boolean = false): string {
  const AWAITING_REVIEW = 'Awaiting review';
  const NEEDS_CHANGES = 'Needs changes';

  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'BASIC_LAUNCH':
      return 'Basic Launch';
    case 'BASIC_LAUNCH_APPROVED':
      return isAdmin ? 'Waiting for Full Requirements implementation' : NEEDS_CHANGES;
    case 'BASIC_LAUNCH_MIXED_RESULT':
      return isAdmin ? 'Basic Launch mixed results' : 'Basic Launch evaluation';
    case 'BASIC_LAUNCH_AWAITING_DEVELOPER_CHANGE':
      return isAdmin ? 'Waiting for Full Requirements changes' : NEEDS_CHANGES;
    case 'BASIC_LAUNCH_REQUIRES_APPROVAL':
      return isAdmin ? 'Under Basic Review' : AWAITING_REVIEW;
    case 'FINALIZED':
      return 'Archived';
    case 'RELEASED':
      return 'Released';
    case 'REQUIRES_APPROVAL':
      return isAdmin ? 'Under Full Review' : AWAITING_REVIEW;
    case 'NEW_SUBMISSION':
      return isAdmin ? 'Awaiting Moderator Approval' : AWAITING_REVIEW;
    case 'AWAITING_DEVELOPER_CHANGE':
      return NEEDS_CHANGES;
    case 'PREPARING_RELEASE':
      return 'Preparing for Release';
    case 'REJECTED':
      return 'Rejected';
    case 'BASIC_LAUNCH_SYNC_REQUIRED':
      return isAdmin ? 'Update under Verification (Basic-Full Launch)' : AWAITING_REVIEW;
    case 'BASIC_LAUNCH_MIXED_RESULT_SYNC_REQUIRED':
      return isAdmin ? 'Update under Verification (Basic Launch Mixed Results)' : AWAITING_REVIEW;
    case 'SYNC_REQUIRED':
      return isAdmin ? 'Update under Verification (Released)' : AWAITING_REVIEW;
    case 'NEW_BUSINESS_REVIEW':
      return 'Waiting for business review';
    case 'UNDER_BUSINESS_REVIEW':
      return 'Under business review';
  }
}

export function readableFlow(flow: boolean | null): string {
  if (flow === null) {
    return 'Full Launch';
  }
  return flow ? 'Full Launch' : 'Basic Launch';
}

export function readableGameStatus(status: GAME_STATUS): string {
  switch (status) {
    case 'SUBMISSION':
      return 'Submission';
    case 'DRAFT':
      return 'Draft';
    case 'BASIC_LAUNCH':
      return 'Basic Launch';
    case 'PUBLISHED':
      return 'Published';
    case 'ARCHIVED':
      return 'Archived';
    case 'UNAVAILABLE':
      return 'Unavailable';
  }
}

export type SubmissionFilterType =
  | 'ALL'
  | 'DRAFT'
  | 'ALL_PENDING'
  | 'RELEASED'
  | 'REJECTED'
  | 'SYNC_REQUIRED'
  | 'REQUIRES_APPROVAL'
  | 'PREPARING_RELEASE'
  | 'AWAITING_DEVELOPER_CHANGE'
  | 'NEW_SUBMISSION'
  | 'NEW_BUSINESS_REVIEW'
  | 'UNDER_BUSINESS_REVIEW';

export const SUBMISSION_FILTERS: { [key: string]: SubmissionFilterType } = {
  all: 'ALL',
  draft: 'DRAFT',
  allPending: 'ALL_PENDING',
  released: 'RELEASED',
  rejected: 'REJECTED',
  syncRequired: 'SYNC_REQUIRED',
  requiresApproval: 'REQUIRES_APPROVAL',
  preparingRelease: 'PREPARING_RELEASE',
  awaitingDeveloperChange: 'AWAITING_DEVELOPER_CHANGE',
  newSubmission: 'NEW_SUBMISSION',
  newBusinessReview: 'NEW_BUSINESS_REVIEW',
  underBusinessReview: 'UNDER_BUSINESS_REVIEW',
};

export function filterByStatus(status: SUBMISSION_STATUS, filter: SubmissionFilterType): boolean {
  const statusFilter = (statuses: SUBMISSION_STATUS[]) => {
    return statuses.indexOf(status) >= 0;
  };
  switch (filter) {
    case 'ALL':
      return true;
    case 'DRAFT':
      return statusFilter(['DRAFT']);
    case 'ALL_PENDING':
      return statusFilter([
        'REQUIRES_APPROVAL',
        'PREPARING_RELEASE',
        'AWAITING_DEVELOPER_CHANGE',
        'NEW_SUBMISSION',
        'NEW_BUSINESS_REVIEW',
        'UNDER_BUSINESS_REVIEW',
        'BASIC_LAUNCH',
        'BASIC_LAUNCH_APPROVED',
        'BASIC_LAUNCH_AWAITING_DEVELOPER_CHANGE',
        'BASIC_LAUNCH_REQUIRES_APPROVAL',
        'BASIC_LAUNCH_SYNC_REQUIRED',
      ]);
    case 'REJECTED':
      return statusFilter(['REJECTED']);
    case 'RELEASED':
      return statusFilter(['RELEASED', 'SYNC_REQUIRED']);
    case 'SYNC_REQUIRED':
      return statusFilter(['SYNC_REQUIRED']);
    case 'REQUIRES_APPROVAL':
      return statusFilter(['REQUIRES_APPROVAL']);
    case 'PREPARING_RELEASE':
      return statusFilter(['PREPARING_RELEASE']);
    case 'AWAITING_DEVELOPER_CHANGE':
      return statusFilter(['AWAITING_DEVELOPER_CHANGE']);
    case 'NEW_SUBMISSION':
      return statusFilter(['NEW_SUBMISSION']);
    default:
      throw new Error(`Unsupported filter: ${filter}`);
  }
}

export function hadBasicLaunch(status: SUBMISSION_STATUS): boolean {
  return status.startsWith('BASIC_LAUNCH');
}
